import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import SubscriptionGate from "./components/SubscriptionGate";
import { Toaster } from "./components/ui/sonner";
import SupportChat from "./components/SupportChat";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import DashboardPage from "./pages/DashboardPage";
import TransactionsPage from "./pages/TransactionsPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import RulesEnginePage from "./pages/RulesEnginePage";
import CorrectionsPage from "./pages/CorrectionsPage";
import SettingsPage from "./pages/SettingsPage";
import ClientProfilesPage from "./pages/ClientProfilesPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import KnowledgeGraphPage from "./pages/KnowledgeGraphPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import BudgetPage from "./pages/BudgetPage";
import RecurringPage from "./pages/RecurringPage";
import JournalEntriesPage from "./pages/JournalEntriesPage";
import OnboardingWizard from "./pages/OnboardingWizard";
import FinancialStatementsPage from "./pages/FinancialStatementsPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import BankFeedsPage from "./pages/BankFeedsPage";
import CurrencyPage from "./pages/CurrencyPage";
import SalesInboxPage from "./pages/SalesInboxPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SubscriptionAgreementPage from "./pages/SubscriptionAgreementPage";
import ReconCenterPage from "./pages/ReconCenterPage";
import CloseReadinessPage from "./pages/CloseReadinessPage";
import ExceptionQueuePage from "./pages/ExceptionQueuePage";
import CreditCardPage from "./pages/CreditCardPage";
import MissingDocumentsPage from "./pages/MissingDocumentsPage";
import AuthCallback from "./pages/AuthCallback";
import LandingPage from "./pages/LandingPage";
import SolutionPage from "./pages/SolutionPage";
import ConnectPage from "./pages/ConnectPage";
import AboutPage from "./pages/AboutPage";
import ResourcesPage from "./pages/ResourcesPage";
import AlertsPage from "./pages/AlertsPage";
import ImportBotkeeperPage from "./pages/ImportBotkeeperPage";
import FromBotkeeperPage from "./pages/FromBotkeeperPage";
import TourPlayerPage from "./pages/TourPlayerPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Auth-protected route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F0' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#C69C6D', borderTopColor: 'transparent' }}></div>
          <p className="text-sm font-body" style={{ color: '#A1A1A5' }}>LOADING</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Auth + Subscription protected route (paywall for non-dashboard)
function PaidRoute({ children }) {
  return (
    <ProtectedRoute>
      <SubscriptionGate>{children}</SubscriptionGate>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<PublicHome />} />
      <Route path="/solutions/:slug" element={<SolutionPage />} />
      <Route path="/connect" element={<ConnectPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/from-botkeeper" element={<FromBotkeeperPage />} />
      <Route path="/tour" element={<TourPlayerPage />} />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/subscription-agreement" element={<SubscriptionAgreementPage />} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Dashboard: always accessible (read-only when expired) */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/import/botkeeper" element={<ProtectedRoute><ImportBotkeeperPage /></ProtectedRoute>} />
      
      {/* All other routes: require active subscription */}
      <Route path="/transactions" element={<PaidRoute><TransactionsPage /></PaidRoute>} />
      <Route path="/review" element={<PaidRoute><ReviewQueuePage /></PaidRoute>} />
      <Route path="/rules" element={<PaidRoute><RulesEnginePage /></PaidRoute>} />
      <Route path="/corrections" element={<PaidRoute><CorrectionsPage /></PaidRoute>} />
      <Route path="/settings" element={<PaidRoute><SettingsPage /></PaidRoute>} />
      <Route path="/clients" element={<PaidRoute><ClientProfilesPage /></PaidRoute>} />
      <Route path="/audit" element={<PaidRoute><AuditTrailPage /></PaidRoute>} />
      <Route path="/knowledge-graph" element={<PaidRoute><KnowledgeGraphPage /></PaidRoute>} />
      <Route path="/analytics" element={<PaidRoute><AnalyticsPage /></PaidRoute>} />
      <Route path="/budget" element={<PaidRoute><BudgetPage /></PaidRoute>} />
      <Route path="/recurring" element={<PaidRoute><RecurringPage /></PaidRoute>} />
      <Route path="/journal-entries" element={<PaidRoute><JournalEntriesPage /></PaidRoute>} />
      <Route path="/financial-statements" element={<PaidRoute><FinancialStatementsPage /></PaidRoute>} />
      <Route path="/reconciliation" element={<PaidRoute><ReconciliationPage /></PaidRoute>} />
      <Route path="/recon-center" element={<PaidRoute><ReconCenterPage /></PaidRoute>} />
      <Route path="/close-readiness" element={<PaidRoute><CloseReadinessPage /></PaidRoute>} />
      <Route path="/exception-queue" element={<PaidRoute><ExceptionQueuePage /></PaidRoute>} />
      <Route path="/credit-card" element={<PaidRoute><CreditCardPage /></PaidRoute>} />
      <Route path="/missing-documents" element={<PaidRoute><MissingDocumentsPage /></PaidRoute>} />
      <Route path="/onboarding" element={<PaidRoute><OnboardingWizard /></PaidRoute>} />
      <Route path="/bank-feeds" element={<PaidRoute><BankFeedsPage /></PaidRoute>} />
      <Route path="/currency" element={<PaidRoute><CurrencyPage /></PaidRoute>} />
      <Route path="/sales-inbox" element={<PaidRoute><SalesInboxPage /></PaidRoute>} />
    </Routes>
  );
}

// If user is logged in, `/` redirects to dashboard; otherwise shows landing.
function PublicHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" />
          <SupportChat />
        </div>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
